// ============================================================
// Search UX helpers - highlighting, recent searches, analytics
// logging. Kept tiny and dependency-free.
// ============================================================


// Splits `text` into [matched, unmatched] segments where any
// token from `query` (length ≥ 2) is wrapped. Returns an array
// of { text, match } objects so React can render with bold spans.
export const highlightSegments = (text, query) => {
  if (!text) return [{ text: '', match: false }];
  if (!query) return [{ text, match: false }];
  const tokens = (query || '')
    .toLowerCase()
    .split(/[^a-z0-9$]+/)
    .filter((t) => t.length >= 2);
  if (tokens.length === 0) return [{ text, match: false }];

  // Sort tokens longest-first so we don't double-match.
  const sorted = [...new Set(tokens)].sort((a, b) => b.length - a.length);
  // Build a single regex with word-boundary-ish matching.
  const escaped = sorted.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');

  const out = [];
  let lastIdx = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) out.push({ text: text.slice(lastIdx, match.index), match: false });
    out.push({ text: match[0], match: true });
    lastIdx = match.index + match[0].length;
    if (match.index === re.lastIndex) re.lastIndex += 1;
  }
  if (lastIdx < text.length) out.push({ text: text.slice(lastIdx), match: false });
  return out.length ? out : [{ text, match: false }];
};

// ─── Recent searches (localStorage) ─────────────────────────

const RECENT_KEY = 'bp_recent_searches_v1';
const RECENT_MAX = 5;

export const getRecentSearches = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
};

export const pushRecentSearch = (query) => {
  if (typeof window === 'undefined') return;
  const q = (query || '').trim();
  if (!q || q.length < 2) return;
  try {
    const current = getRecentSearches();
    const next = [q, ...current.filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota errors */
  }
  // 2026-02-15 Slice A: also fire to Supabase user_events so HQ has
  // a server-side view of what people are searching for.
  try {
    // lazy require to keep this util importable from anywhere
    // eslint-disable-next-line global-require
    const { trackEvent } = require('../lib/trackEvent');
    trackEvent('search', { query: q });
  } catch { /* swallow */ }
};

export const clearRecentSearches = () => {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
};

// ─── Analytics - fire-and-forget POST ───────────────────────
//
// Called from the search bar (debounced) and from the /search
// page on submit. Backend logs the query plus zero/non-zero
// result count so the team can see content gaps. Errors are
// swallowed - analytics never break UX.
const ANALYTICS_DEBOUNCE_MS = 1500;
let _analyticsTimer = null;
let _lastLoggedQ = '';

export const logSearchQuery = ({ query, resultCount, source = 'search-bar' }) => {
  if (typeof window === 'undefined') return;
  const q = (query || '').trim();
  if (!q || q.length < 2) return;
  // Clear any pending (prefix) timer BEFORE the dedup check, otherwise a timer
  // queued for an earlier prefix can still fire when the user retypes a query
  // equal to the last one logged — causing a duplicate builder_search event.
  if (_analyticsTimer) clearTimeout(_analyticsTimer);
  if (q === _lastLoggedQ) return;
  _analyticsTimer = setTimeout(() => {
    _lastLoggedQ = q;
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    try {
      // Beacon if available - survives navigation. JSON otherwise.
      const body = JSON.stringify({ query: q, result_count: resultCount, source });
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${url}/api/search/log`, blob);
      } else {
        fetch(`${url}/api/search/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* never throw from analytics */
    }
  }, ANALYTICS_DEBOUNCE_MS);
};

// ─── Trending searches (cached) ─────────────────────────────
//
// Pulls top recent queries from the backend so we can show a
// "Trending now" section in the empty state. Cached for 5 min
// in memory so each focus doesn't refetch.
let _trendingCache = { value: null, ts: 0 };
const TRENDING_TTL_MS = 5 * 60 * 1000;

export const fetchTrendingSearches = async () => {
  const now = Date.now();
  if (_trendingCache.value && now - _trendingCache.ts < TRENDING_TTL_MS) {
    return _trendingCache.value;
  }
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) return [];
  try {
    const res = await fetch(`${url}/api/search/trending?days=7&limit=6`);
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data?.queries) ? data.queries : [];
    _trendingCache = { value: list, ts: now };
    return list;
  } catch {
    return [];
  }
};

// ─── Voice input (Web Speech API) ───────────────────────────

export const supportsVoiceInput = () =>
  typeof window !== 'undefined' &&
  ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

export const startVoiceInput = ({ onResult, onError, onEnd }) => {
  const SR = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  if (!SR) {
    onError?.('not-supported');
    return null;
  }
  const recog = new SR();
  recog.lang = 'en-AU';
  recog.interimResults = true;
  recog.continuous = false;
  recog.maxAlternatives = 1;
  recog.onresult = (e) => {
    let transcript = '';
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      transcript += e.results[i][0].transcript;
    }
    onResult?.(transcript, e.results[e.results.length - 1].isFinal);
  };
  recog.onerror = (e) => onError?.(e.error || 'unknown');
  recog.onend = () => onEnd?.();
  recog.start();
  return recog;
};
